import {
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { deleteObject, ref } from 'firebase/storage';
import { auth, firestore, storage } from '../firebase';
import { promotionContent, type AdminPromotion, type PromotionDraft, type PublishedSnapshot } from '../utils/promotionAdmin';
import { normalizeActivity } from '../hooks/useFeed';
import type { TonyteActivity } from '../types';

// In-app promotion management (admin/co-admin). Mirrors
// Tonyte_website/src/promotionsStore.js exactly, so edits made on the phone
// and in the dashboard are interchangeable. Only admins can satisfy any of
// these reads/writes — firestore.rules / storage.rules enforce it.

const DRAFTS = 'promotionDrafts';
const PUBLISHED = 'promotions';

const uid = () => {
  const current = auth.currentUser?.uid;
  if (!current) throw new Error('Not signed in.');
  return current;
};

const toMillis = (value: unknown): number | null => {
  if (value && typeof value === 'object' && typeof (value as { toMillis?: () => number }).toMillis === 'function') {
    return (value as { toMillis: () => number }).toMillis();
  }
  return typeof value === 'number' ? value : null;
};
const toTimestamp = (ms: number | null) => (ms === null ? null : Timestamp.fromMillis(ms));

function parseDraft(data: Record<string, any>): PromotionDraft {
  return {
    title: data.title || '',
    subtitle: data.subtitle || '',
    description: data.description || '',
    ctaLabel: data.ctaLabel || '',
    coverImageUrl: data.coverImageUrl || '',
    coverImagePath: data.coverImagePath || '',
    startAt: toMillis(data.startAt),
    endAt: toMillis(data.endAt),
    priority: Number.isInteger(data.priority) ? data.priority : 10,
    cityKeys: Array.isArray(data.cityKeys) ? data.cityKeys : [],
    eventIds: Array.isArray(data.eventIds) ? data.eventIds : [],
    archived: data.archived === true,
    revision: data.revision || 0,
  };
}

function parsePublished(data: Record<string, any> | undefined): PublishedSnapshot | null {
  if (!data) return null;
  return {
    published: data.published === true,
    startAt: toMillis(data.startAt),
    endAt: toMillis(data.endAt),
    version: data.version || 0,
    sourceRevision: data.sourceRevision || 0,
    publishedAt: toMillis(data.publishedAt),
    coverImagePath: data.coverImagePath || '',
  };
}

export async function listAdminPromotions(): Promise<AdminPromotion[]> {
  const [drafts, published] = await Promise.all([
    getDocs(collection(firestore, DRAFTS)),
    getDocs(collection(firestore, PUBLISHED)),
  ]);
  const byId = new Map(published.docs.map(d => [d.id, parsePublished(d.data())]));
  return drafts.docs.map(d => ({ id: d.id, draft: parseDraft(d.data()), published: byId.get(d.id) ?? null }));
}

export async function getAdminPromotion(id: string): Promise<AdminPromotion | null> {
  const [draftSnap, publishedSnap] = await Promise.all([
    getDoc(doc(firestore, DRAFTS, id)),
    getDoc(doc(firestore, PUBLISHED, id)),
  ]);
  if (!draftSnap.exists()) return null;
  return { id, draft: parseDraft(draftSnap.data()), published: publishedSnap.exists() ? parsePublished(publishedSnap.data()) : null };
}

export function newPromotionId(): string {
  return doc(collection(firestore, DRAFTS)).id;
}

export async function saveAdminDraft(id: string, draft: PromotionDraft, isNew: boolean): Promise<AdminPromotion> {
  const by = uid();
  const payload = {
    ...promotionContent(draft),
    startAt: toTimestamp(draft.startAt),
    endAt: toTimestamp(draft.endAt),
    archived: draft.archived,
    updatedAt: serverTimestamp(),
    updatedBy: by,
  };
  if (isNew) await setDoc(doc(firestore, DRAFTS, id), { ...payload, revision: 1, createdAt: serverTimestamp(), createdBy: by });
  else await updateDoc(doc(firestore, DRAFTS, id), { ...payload, revision: increment(1) });
  return (await getAdminPromotion(id))!;
}

export async function publishAdminPromotion(id: string, { startNow = false } = {}): Promise<AdminPromotion> {
  const by = uid();
  const current = await getAdminPromotion(id);
  if (!current) throw new Error('Save the promotion before publishing.');
  const startAt = startNow ? Date.now() : current.draft.startAt;
  const batch = writeBatch(firestore);
  batch.set(doc(firestore, PUBLISHED, id), {
    ...promotionContent(current.draft),
    startAt: toTimestamp(startAt),
    endAt: toTimestamp(current.draft.endAt),
    published: true,
    version: (current.published?.version || 0) + 1,
    sourceRevision: current.draft.revision,
    publishedAt: serverTimestamp(),
    publishedBy: by,
    updatedAt: serverTimestamp(),
  });
  batch.update(doc(firestore, DRAFTS, id), {
    ...(startNow ? { startAt: toTimestamp(startAt) } : {}),
    lastPublishedAt: serverTimestamp(),
    lastPublishedBy: by,
  });
  await batch.commit();
  return (await getAdminPromotion(id))!;
}

export async function unpublishAdminPromotion(id: string): Promise<AdminPromotion> {
  await updateDoc(doc(firestore, PUBLISHED, id), { published: false, updatedAt: serverTimestamp(), unpublishedBy: uid() });
  return (await getAdminPromotion(id))!;
}

export async function setAdminPromotionArchived(id: string, archived: boolean): Promise<AdminPromotion> {
  const by = uid();
  const current = await getAdminPromotion(id);
  const batch = writeBatch(firestore);
  if (archived && current?.published?.published) {
    batch.update(doc(firestore, PUBLISHED, id), { published: false, updatedAt: serverTimestamp(), unpublishedBy: by });
  }
  batch.update(doc(firestore, DRAFTS, id), { archived, updatedAt: serverTimestamp(), updatedBy: by });
  await batch.commit();
  return (await getAdminPromotion(id))!;
}

export async function deleteAdminPromotion(id: string): Promise<void> {
  const current = await getAdminPromotion(id);
  const batch = writeBatch(firestore);
  batch.delete(doc(firestore, PUBLISHED, id));
  batch.delete(doc(firestore, DRAFTS, id));
  await batch.commit();
  const paths = new Set([current?.draft.coverImagePath, current?.published?.coverImagePath].filter(Boolean) as string[]);
  await Promise.all([...paths].map(path => deleteObject(ref(storage, path)).catch(() => {})));
}

// Removes a replaced/abandoned cover, never one still used by the draft or
// the live version.
export async function deleteUnusedPromotionCover(path: string, keep: string[]): Promise<void> {
  if (!path || keep.includes(path) || !path.startsWith('promotions/')) return;
  await deleteObject(ref(storage, path)).catch(() => {});
}

// Referenced events missing from the (upcoming-only) feed pool — typically
// ones that have ended — fetched individually so the editor can tell
// "ended, prune it" apart from "deleted / not visible".
export async function fetchEventsByIds(ids: string[]): Promise<TonyteActivity[]> {
  const results = await Promise.all(ids.map(async id => {
    try {
      const snap = await getDoc(doc(firestore, 'activities', id));
      return snap.exists() ? normalizeActivity(snap.id, snap.data(), 'activities') : null;
    } catch {
      return null;
    }
  }));
  return results.filter((e): e is TonyteActivity => Boolean(e));
}
